'use client';

import type { PrV2SummaryResponse } from '@/lib/pr-v2/types';
import type { QueryClient } from '@tanstack/react-query';

import type { InboxPullRequest } from '../types';

import { setPrV2CacheSource } from '@/data/v2/pr/prefetch-debug';
import { prV2SummaryQueryKey } from '@/data/v2/pr/query-keys';
import { parsePrV2Metadata } from '@/lib/pr-v2/metadata-summary';

/**
 * Populates the TanStack Query summary cache with data already available from the inbox.
 * Skips if the cache already has data (e.g. from hover prefetch).
 * The data is immediately stale so usePrV2Summary fetches the full data in the background.
 */
export function populatePrSummaryFromInbox(queryClient: QueryClient, pr: InboxPullRequest): void {
  const owner = pr.repository.owner;
  const repo = pr.repository.name;
  const number = pr.number.toString();

  const queryKey = prV2SummaryQueryKey(owner, repo, number);
  const existing = queryClient.getQueryData(queryKey);
  if (existing) {
    return;
  }

  const metadata = {
    pullRequest: {
      body: null,
      created_at: pr.createdAt,
      updated_at: pr.updatedAt,
      head: { ref: pr.head.ref },
      base: { ref: pr.base.ref },
      changed_files: pr.changedFiles,
      additions: pr.additions,
      deletions: pr.deletions,
    },
  };

  const seed: PrV2SummaryResponse = {
    syncState: 'ready',
    liveMetadataRefreshedAt: null,
    discussionCachedAt: null,
    mergeabilityStatus: null,
    pullRequest: {
      id: '',
      owner,
      repo,
      number: pr.number,
      installationId: null,
      title: pr.title,
      state: pr.state,
      draft: pr.draft,
      merged: Boolean(pr.mergedAt),
      url: pr.url,
      author: {
        login: pr.author,
        avatarUrl: pr.avatarUrl,
      },
      headSha: pr.head.sha ?? '',
      baseSha: pr.base.sha ?? '',
      cachedAt: new Date().toISOString(),
      githubUpdatedAt: pr.updatedAt,
      metadata,
      metadataSummary: parsePrV2Metadata(metadata),
      counts: { reviews: 0, comments: 0, checks: pr.checks?.total ?? 0 },
    },
  };

  queryClient.setQueryData(queryKey, seed, { updatedAt: 0 });
  setPrV2CacheSource(queryKey, 'seed:inbox-click');
}
